import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { MochiInput } from '@autopass-web/mochi-component';
import {
  taxIdNumberFormatStrategy,
  taxIdNumberLengthStrategy,
  taxIdNumberLogicStrategy,
} from '@autopass-web/mochi-utils';

import { useImperativeForm } from '../../../hooks/use-bind-async-submit';
import { emailSchema } from '../utils';
import { EmailInput } from './UI';

import type { ReceiptInfoBase } from '../../../configs/receipts';
import type { EmailFieldConfig, ReceiptCompProps } from '../utils';

export interface B2bReceiptInfo extends ReceiptInfoBase {
  type: 'b2b';
  ubn: string;
  email: string;
}

type B2bReceiptForm = Omit<B2bReceiptInfo, 'type'>;

type B2bReceiptProps = ReceiptCompProps<{
  emailFieldConfig: EmailFieldConfig;
}>;

export const B2bReceipt = ({
  controlRef,
  emailFieldConfig = 'default',
  storage,
  formMode,
}: B2bReceiptProps) => {
  const schema =
    emailFieldConfig === 'never'
      ? z.object({
          ubn: ubnSchema,
        })
      : z.object({
          ubn: ubnSchema,
          email: emailSchema.or(z.string().length(0)),
        });

  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    clearErrors,
    subscribe,
  } = useForm<B2bReceiptForm>({
    defaultValues: { ...(storage?.get?.() ?? {}) },
    mode: formMode ?? 'onSubmit',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const receiptInfo = storage?.get?.();

        storage?.set({ ...receiptInfo, ...values, type: 'b2b' });
      },
    });

    return () => cb();
  }, [subscribe]);

  const formProps = useImperativeForm(
    controlRef,
    errors,
    (submit) => {
      return handleSubmit((data) => submit({ ...data, type: 'b2b' }));
    },
    'b2b',
  );

  return (
    <form {...formProps}>
      <MochiInput
        variant="grey"
        inputMode="numeric"
        placeholder="請輸入 8 位數統一編號"
        maxLength={8}
        errorMessage={errors?.ubn?.message}
        {...register('ubn', {
          onBlur() {
            trigger('ubn');
          },
          onChange(e) {
            const value = e.target.value;
            clearErrors('ubn');
            value.length > 8 && trigger('ubn');
          },
        })}
      />
      <EmailInput
        config={emailFieldConfig}
        errorMessage={errors.email?.message}
        hint={
          '欲索取「統編發票」，請填寫 Email 信箱，成功付款後將寄送三聯式電子發票給您'
        }
        {...register('email')}
      />
    </form>
  );
};

const ubnSchema = z
  .string()
  .min(1, {
    message: '此欄位為必填',
  })
  .refine((v) => taxIdNumberLengthStrategy(v) && taxIdNumberFormatStrategy(v), {
    message: '輸入格式錯誤，需為 8 位數字組成',
  })
  .refine(taxIdNumberLogicStrategy, {
    message: '統一編號輸入錯誤，請重新檢查',
  });
